import { RadioGroup, RadioGroupItem } from "../ui/RadioGroup";
import { useAtom } from "jotai";
import { paymentOptionAtom } from "./store";
import type { GetMarketplaceCalculatedPriceQueryResult } from "@repo/rq-api-public";
import { PaymentOption } from "./types";
import { ReactNode, useEffect } from "react";
import {
  FieldLabel,
  Field,
  FieldContent,
  FieldTitle,
  FieldDescription,
} from "../ui/Field";
import { formatCurrency } from "../../utils/currency";
import { ccn } from "../../styles/utils";
import { textStyles } from "../../styles/typography";

export default function PrePaymentRadioGroup({
  calculation,
  locale,
  currency,
}: {
  calculation: GetMarketplaceCalculatedPriceQueryResult;
  locale: string;
  currency: string;
}) {
  const { prePayment } = calculation;
  const [paymentOption, setPaymentOption] = useAtom(paymentOptionAtom);

  useEffect(
    () => {
      if (!calculation.prePayment) setPaymentOption(PaymentOption.Full);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calculation]
  );

  if (!prePayment) return null;

  return (
    <RadioGroup
      value={paymentOption}
      onValueChange={(val) => setPaymentOption(val as PaymentOption)}
      className="gap-1"
    >
      <RadioContained
        value={PaymentOption.Full}
        title="Pay in full"
        description={
          <>
            {formatCurrency(calculation.totalPrice ?? 0, currency, locale)} +
            processing fee
          </>
        }
      />
      <RadioContained
        value={PaymentOption.Deposit}
        title={`${prePayment.percentage}% deposit`}
        description={
          <>
            {formatCurrency(prePayment.amount!, currency, locale)} now +
            processing fee, rest before trip
          </>
        }
      />
    </RadioGroup>
  );
}

function RadioContained({
  description,
  title,
  value,
}: {
  value: PaymentOption;
  title: ReactNode;
  description: ReactNode;
}) {
  const radioId = `payment-${value}`;
  return (
    <FieldLabel htmlFor={radioId} className="bg-opacity-70 border-grey-light">
      <Field
        orientation="horizontal"
        className="p-2 items-center! cursor-pointer"
      >
        <RadioGroupItem value={value} id={radioId} className="text-blue-dark" />
        <FieldContent className="gap-2">
          <FieldTitle className={ccn(textStyles.sub1, "text-blue-dark")}>
            {title}
          </FieldTitle>
          <FieldDescription
            className={ccn(textStyles.label, "text-blue-dark60")}
          >
            {description}
          </FieldDescription>
        </FieldContent>
      </Field>
    </FieldLabel>
  );
}
